import { useMemo, useState } from "react";
import { AxiosError } from "axios";

import {
  useBulkApproveTimesheets,
  useVenueTimesheets,
} from "@/features/timesheets/model/useTimesheets";
import type { Timesheet, TimesheetStatus } from "@/features/timesheets/types";
import { ROLE_LABELS, type ShiftRole } from "@/features/shifts/types";
import { DisputeDialog } from "@/features/timesheets/ui/DisputeDialog";
import { TimesheetReviewRow } from "@/features/timesheets/ui/TimesheetReviewRow";
import { Button } from "@/shared/ui";

const TABS: { value: TimesheetStatus; label: string }[] = [
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "APPROVED", label: "Approved" },
  { value: "DISPUTED", label: "Disputed" },
];

function fmtShiftDate(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function isFlagged(t: Timesheet): boolean {
  return t.off_site_checkout || t.no_gps || t.auto_closed || t.manual_by_venue;
}

export function VenueTimesheetsPage() {
  const [tab, setTab] = useState<TimesheetStatus>("PENDING_REVIEW");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [disputeTarget, setDisputeTarget] = useState<Timesheet | null>(null);

  const { data: timesheets, isLoading, error } = useVenueTimesheets(tab);
  const bulkApprove = useBulkApproveTimesheets();

  const visible = timesheets ?? [];
  const unflaggedPending = useMemo(
    () => visible.filter((t) => t.status === "PENDING_REVIEW" && !isFlagged(t)),
    [visible],
  );

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectAllUnflagged = () => {
    setSelectedIds(new Set(unflaggedPending.map((t) => t.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const submitBulkApprove = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    bulkApprove.mutate(ids, {
      onSuccess: () => clearSelection(),
    });
  };

  const bulkErr = bulkApprove.error
    ? bulkApprove.error instanceof AxiosError
      ? (bulkApprove.error.response?.data?.detail ?? "Couldn't bulk-approve")
      : bulkApprove.error.message
    : null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-copy">Timesheets</h1>
        <p className="mt-1 text-sm text-copy-muted">
          Review hours your workers submit at check-out. Approving moves payroll forward.
        </p>
      </div>

      <div role="tablist" className="flex border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.value}
            role="tab"
            aria-selected={tab === t.value}
            onClick={() => {
              setTab(t.value);
              clearSelection();
            }}
            className={
              "px-4 py-2 text-sm font-medium transition-colors " +
              (tab === t.value
                ? "border-b-2 border-primary text-primary"
                : "text-copy-muted hover:text-copy")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "PENDING_REVIEW" && unflaggedPending.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface p-3 shadow-card">
          <div className="text-sm text-copy">
            {selectedIds.size > 0 ? (
              <>
                <span className="font-medium">{selectedIds.size}</span> selected
              </>
            ) : (
              <>
                <span className="font-medium">{unflaggedPending.length}</span> unflagged pending
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedIds.size === 0 ? (
              <Button variant="outline" size="sm" onClick={selectAllUnflagged}>
                Select all unflagged
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear selection
              </Button>
            )}
            <Button
              variant="success"
              size="sm"
              onClick={submitBulkApprove}
              disabled={selectedIds.size === 0 || bulkApprove.isPending}
            >
              {bulkApprove.isPending ? "Approving…" : `Approve ${selectedIds.size || ""}`.trim()}
            </Button>
          </div>
        </div>
      )}

      {bulkErr && <p className="text-sm text-destructive">{bulkErr}</p>}

      {isLoading && <div className="text-sm text-copy-muted">Loading…</div>}
      {error && <div className="text-sm text-destructive">Could not load timesheets.</div>}

      {!isLoading && !error && visible.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center shadow-card">
          <p className="text-sm font-medium text-copy">
            {tab === "PENDING_REVIEW"
              ? "No timesheets pending review"
              : tab === "APPROVED"
                ? "No approved timesheets yet"
                : "No disputes"}
          </p>
          <p className="mt-1 text-sm text-copy-muted">
            {tab === "PENDING_REVIEW"
              ? "Timesheets appear here as soon as a worker checks out."
              : "They'll show up here once you act on pending ones."}
          </p>
        </div>
      )}

      <div className="space-y-2">
        {visible.map((t) => (
          <TimesheetReviewRow
            key={t.id}
            timesheet={t}
            workerName={t.worker_name ?? undefined}
            shiftRole={t.shift_role ? ROLE_LABELS[t.shift_role as ShiftRole] : undefined}
            shiftDate={fmtShiftDate(t.shift_date)}
            scheduledStart={t.shift_start_time ?? undefined}
            scheduledEnd={t.shift_end_time ?? undefined}
            selected={selectedIds.has(t.id)}
            onSelectChange={(checked) => toggleSelect(t.id, checked)}
            onDispute={(target) => setDisputeTarget(target)}
          />
        ))}
      </div>

      {disputeTarget && (
        <DisputeDialog timesheet={disputeTarget} onClose={() => setDisputeTarget(null)} />
      )}
    </div>
  );
}
